import { FONT_MONO } from '../../config/fonts';
import { useState } from "react";
import {
  Box,
  Collapse,
  IconButton,
  Popover,
  Slider,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";

import { SIDEBAR_WIDTH } from "./sidebarWidths";
import { CLOCK_THEMES } from "./MapClock";

// Building blocks shared by the Radar settings and Map settings sidebars: a dark panel that
// matches the map overlays, collapsible sections, and one row style per kind of control.
// Everything takes `accent` (teal on Radar, amber on Review).

export const accentOf = (mode) => (CLOCK_THEMES[mode] || CLOCK_THEMES.live).accent;

const MUTED = "rgba(255,255,255,0.6)";

export const SidebarPanel = ({ children, accent, width = SIDEBAR_WIDTH, sx }) => (
  <Box
    sx={{
      position: "absolute",
      right: 0,
      top: 0,
      width: `${width}px`,
      height: "100%",
      zIndex: 999,
      boxSizing: "border-box",
      overflowY: "auto",
      px: 1.5,
      py: 1.25,
      color: "#fff",
      backgroundColor: "rgba(10, 14, 18, 0.86)",
      backdropFilter: "blur(6px)",
      borderLeft: `1px solid ${alpha(accent, 0.3)}`,
      boxShadow: "-8px 0 24px rgba(0,0,0,0.35)",
      scrollbarWidth: "thin",
      scrollbarColor: "rgba(255,255,255,0.2) transparent",
      ...sx,
    }}
  >
    {children}
  </Box>
);

export const SidebarSection = ({ icon: Icon, title, accent, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Box
      sx={{
        mb: 1.25,
        borderRadius: "10px",
        border: "1px solid rgba(255,255,255,0.08)",
        backgroundColor: "rgba(255,255,255,0.03)",
        overflow: "hidden",
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); } }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.25,
          py: 0.9,
          cursor: "pointer",
          userSelect: "none",
          borderBottom: open ? "1px solid rgba(255,255,255,0.08)" : "none",
          "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
        }}
      >
        {Icon && <Icon sx={{ fontSize: "1.1rem", color: accent }} />}
        <Typography sx={{ flex: 1, fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase" }}>
          {title}
        </Typography>
        <IconButton size="small" tabIndex={-1} sx={{ color: MUTED, p: 0.25 }}>
          <ExpandMore sx={{ fontSize: "1.1rem", transform: open ? "none" : "rotate(-90deg)", transition: "transform .2s" }} />
        </IconButton>
      </Box>

      <Collapse in={open}>
        <Box sx={{ px: 1.25, py: 0.75 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

const rowLabelSx = { fontSize: "0.8rem", color: "rgba(255,255,255,0.88)" };

export const SwitchSetting = ({ label, checked, onChange, accent, hint }) => (
  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ py: 0.25 }}>
    <Box minWidth={0}>
      <Typography sx={rowLabelSx}>{label}</Typography>
      {hint && <Typography sx={{ fontSize: "0.66rem", color: MUTED }}>{hint}</Typography>}
    </Box>
    <Switch
      size="small"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      sx={{
        "& .MuiSwitch-switchBase.Mui-checked": { color: accent },
        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: accent, opacity: 0.5 },
        "& .MuiSwitch-track": { backgroundColor: "rgba(255,255,255,0.35)" },
      }}
    />
  </Box>
);

// Label + value on one line, full-width slider below (the sidebar is narrow)
export const SliderSetting = ({
  label, value, onChange, onChangeCommitted, min, max, step = 1, accent, format = (v) => v, disabled = false,
}) => (
  <Box sx={{ py: 0.5, opacity: disabled ? 0.45 : 1 }}>
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography sx={rowLabelSx}>{label}</Typography>
      <Box
        sx={{
          px: 0.75,
          borderRadius: "6px",
          fontSize: "0.72rem",
          fontWeight: 700,
          fontFamily: FONT_MONO,
          color: accent,
          backgroundColor: alpha(accent, 0.12),
        }}
      >
        {format(value)}
      </Box>
    </Box>
    <Slider
      size="small"
      value={value}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      onChange={(e, v) => onChange(v)}
      onChangeCommitted={onChangeCommitted ? (e, v) => onChangeCommitted(v) : undefined}
      sx={{
        mt: -0.25,
        color: accent,
        "& .MuiSlider-thumb": { width: 12, height: 12 },
        "& .MuiSlider-rail": { opacity: 1, backgroundColor: "rgba(255,255,255,0.18)" },
      }}
    />
  </Box>
);

// Small segmented control (e.g. NONE / TRACK / SYNTH)
export const SegmentedSetting = ({ label, value, onChange, options, accent }) => (
  <Box sx={{ py: 0.5 }}>
    <Typography sx={{ ...rowLabelSx, mb: 0.5 }}>{label}</Typography>
    <ToggleButtonGroup
      exclusive
      fullWidth
      size="small"
      value={value}
      onChange={(e, v) => { if (v !== null) onChange(v); }}
      sx={{
        p: 0.25,
        gap: 0.25,
        borderRadius: "8px",
        backgroundColor: "rgba(255,255,255,0.06)",
        "& .MuiToggleButton-root": {
          py: 0.35,
          border: "none",
          borderRadius: "6px !important",
          fontSize: "0.68rem",
          fontWeight: 700,
          letterSpacing: "0.04em",
          color: MUTED,
          "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.08)" },
          "&.Mui-selected, &.Mui-selected:hover": { color: "#0b0f12", backgroundColor: accent },
        },
      }}
    >
      {options.map((o) => (
        <ToggleButton key={String(o.value)} value={o.value}>{o.label}</ToggleButton>
      ))}
    </ToggleButtonGroup>
  </Box>
);

// Row with a clickable swatch; the editor (color picker, filter sliders...) opens in a pop-up
// next to the sidebar, anchored to the swatch.
export const SwatchSetting = ({ label, sub, swatch, accent, children, popupTitle }) => {
  const [anchor, setAnchor] = useState(null);

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        sx={{ py: 0.5, cursor: "pointer", borderRadius: "6px", "&:hover .swatch": { boxShadow: `0 0 0 2px ${alpha(accent, 0.5)}` } }}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        <Box minWidth={0} flex={1}>
          <Typography sx={rowLabelSx}>{label}</Typography>
          {sub && <Typography sx={{ fontSize: "0.66rem", color: MUTED, fontFamily: FONT_MONO }}>{sub}</Typography>}
        </Box>
        <Box
          className="swatch"
          sx={{
            flexShrink: 0,
            borderRadius: "6px",
            border: "1px solid rgba(255,255,255,0.25)",
            boxShadow: anchor ? `0 0 0 2px ${accent}` : "none",
            transition: "box-shadow .15s",
            overflow: "hidden",
            display: "grid",
            placeItems: "center",
          }}
        >
          {swatch}
        </Box>
      </Box>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "center", horizontal: "left" }}
        transformOrigin={{ vertical: "center", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              ml: -1.5,
              p: 1.5,
              color: "#fff",
              backgroundColor: "rgba(12, 16, 20, 0.96)",
              border: `1px solid ${alpha(accent, 0.35)}`,
              borderRadius: "10px",
              boxShadow: "0 10px 36px rgba(0,0,0,0.55)",
            },
          },
        }}
      >
        {popupTitle && (
          <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.14em", color: accent, mb: 1 }}>
            {popupTitle}
          </Typography>
        )}
        {children}
      </Popover>
    </>
  );
};
